import type { BaseEntityState, ServerListParams } from '@crm/shared';

// Fonte di verita' dei types Person: vivono in personMappers (accanto
// al DTO REST e ai mapper int<->string). Qui li re-esportiamo per
// mantenere l'import path storico usato da tutto il codebase UI.
export type { Person, LeadStatus, MarketingStatus, PersonStatus } from './personMappers';

import type { LeadStatus, MarketingStatus, Person } from './personMappers';

// Costanti UI con label i18n key. Il compilatore verifica che `value`
// sia uno dei membri del corrispondente union type (dimenticanze/aggiunte
// non allineate emergono a compile-time).
//
// SYNC CROSS-LAYER — aggiunte/rimozioni di valori vanno replicate in
// parallelo su:
//   MARKETING_STATUSES -> personMappers.ts (type MarketingStatus + MARKETING_STATUS_TO_INT),
//                        modules/crm-util/.../PersonMarketingStatus.java,
//                        locales/{it,en}.json (person.marketingStatuses.*)
//   LEAD_STATUSES      -> personMappers.ts (type LeadStatus + LEAD_STATUS_TO_INT),
//                        modules/crm-util/.../PersonLeadStatus.java,
//                        locales/{it,en}.json (person.leadStatuses.*)

export const MARKETING_STATUSES: { value: MarketingStatus; label: string }[] = [
  { value: 'lead', label: 'person.marketingStatuses.lead' },
  { value: 'marketing_qualified', label: 'person.marketingStatuses.marketingQualified' },
  { value: 'sales_qualified', label: 'person.marketingStatuses.salesQualified' },
  { value: 'customer', label: 'person.marketingStatuses.customer' },
];

export const LEAD_STATUSES: { value: LeadStatus; label: string }[] = [
  { value: 'nuovo', label: 'person.leadStatuses.nuovo' },
  { value: 'in_lavorazione', label: 'person.leadStatuses.in_lavorazione' },
  { value: 'chiuso', label: 'person.leadStatuses.chiuso' },
];

/** Parametri di lista del contatto: quelli comuni piu' la bacheca. */
export interface PersonListParams extends ServerListParams {
  /**
   * Se valorizzato, restituisce i contatti della bacheca indicata, cioe' quelli collocati in uno
   * degli stati della sua pipeline. Ha precedenza sugli altri filtri.
   */
  kanbanId?: number;
}

export interface PersonState extends BaseEntityState<Person> {
  /** Totale records disponibili lato server (per pagination DataGrid). */
  totalCount: number;
}

/**
 * Payload form Person (create + update partial).
 *
 * Dual-field source — il form mantiene entrambi i campi nel local state:
 *   - `personSourceId`: id della source selezionata da autocomplete (0 se
 *     l'utente ha digitato testo libero, o se non c'è ancora selezione).
 *   - `personSource`: stringa visualizzata. Sempre allineata al testo che
 *     l'utente vede; se l'utente seleziona una option dell'autocomplete
 *     diventa il `name` di quella, se digita libero contiene il testo
 *     digitato (e `personSourceId` resta 0).
 *
 * Il mapper `personCreateToInput`/`personUpdateToInput` decide quale dei
 * due inviare al backend (id prevale; altrimenti name → find-or-create).
 */
export interface PersonCreatePayload {
  /** Codice esterno (identificativo gestionale esterno), esposto dal Form Person. */
  externalCode?: string;
  firstName: string;
  lastName: string;
  location?: string;
  personSourceId: number;
  personSource: string;
  /** Stessa semantica dual-field di personSourceId/personSource (vedi sopra). */
  personChannelId: number;
  personChannel: string;
  leadStatus: LeadStatus;
  marketingStatus: MarketingStatus;
  /**
   * Email e telefono primari dal form di create/edit.
   *
   *   - in CREATE `personCreateToInput` li manda nel POST come `emailAddress`/`phoneNumber`, e il
   *     backend li salva nella stessa transazione della persona: chi crea un contatto da questo
   *     payload non deve fare altro (FILO-56: AddRelationDrawer e LinkVeterinarianDialog li
   *     perdevano perché il salvataggio stava solo in PersonForm);
   *   - in EDIT `personUpdateToInput` li ignora: `PersonForm.handleSubmit` aggiorna il primario
   *     esistente, o lo aggiunge se manca, con i thunk di emailAddress/phone.
   */
  email?: string;
  phone?: string;
}

export interface PersonUpdatePayload extends Partial<PersonCreatePayload> {
  personId: number;
  /**
   * Colonna della bacheca in cui spostare il contatto (0 = fuori dalla board).
   *
   * Sta qui e NON in `PersonCreatePayload`: dalla form di creazione non si sceglie una colonna, e
   * l'unico chiamante è il drag&drop della bacheca, che aggiorna un contatto esistente. Il
   * contratto REST lo accetta anche in POST, ma finché nessuno lo invia il tipo non lo promette.
   */
  currentStateId?: number;
}
